import { ChurnTier, SystemCohortType } from '../../../models/churn-prediction/dto/churn-prediction.dto';
import { translate } from '../../../i18n';

export type SignalFormat = 'risk' | 'binary' | 'subscription';

/**
 * Returns a color scheme based on the churn signal value (0-1).
 * < 0.3 = green (safe), 0.3-0.6 = yellow (watch), > 0.6 = red (at risk)
 */
export function getSignalColorScheme(value: number): string {
  if (value < 0.3) return 'green';
  if (value <= 0.6) return 'yellow';
  return 'red';
}

/**
 * Formats a signal value for display based on its type.
 * - risk: "X.X/10" scale
 * - binary: "Yes" / "No"
 * - subscription: human-readable status label
 */
export function formatSignalValue(value: number, format: SignalFormat): string {
  switch (format) {
    case 'binary':
      return value >= 0.5
        ? translate('churn_prediction.signal_yes')
        : translate('churn_prediction.signal_no');
    case 'subscription':
      if (value === 0) return translate('churn_prediction.signal_sub_active');
      if (value <= 0.5) return translate('churn_prediction.signal_sub_none');
      if (value <= 0.7) return translate('churn_prediction.signal_sub_past_due');
      return translate('churn_prediction.signal_sub_canceled');
    case 'risk':
    default:
      return `${(value * 10).toFixed(1)}/10`;
  }
}

/**
 * Maps churn tiers to Chakra color schemes.
 */
export const TIER_COLORS: Record<ChurnTier, string> = {
  [ChurnTier.SAFE]: 'green',
  [ChurnTier.WATCH]: 'yellow',
  [ChurnTier.AT_RISK]: 'orange',
  [ChurnTier.CRITICAL]: 'red',
};

/**
 * Maps churn tiers to i18n translation keys.
 */
export const TIER_LABEL_KEYS: Record<ChurnTier, string> = {
  [ChurnTier.SAFE]: 'churn_prediction.tier_safe',
  [ChurnTier.WATCH]: 'churn_prediction.tier_watch',
  [ChurnTier.AT_RISK]: 'churn_prediction.tier_at_risk',
  [ChurnTier.CRITICAL]: 'churn_prediction.tier_critical',
};

/**
 * Maps system cohort types to Chakra color schemes.
 */
export const COHORT_TYPE_COLORS: Record<SystemCohortType, string> = {
  [SystemCohortType.CHURN_WATCH]: 'orange',
  [SystemCohortType.CHURN_AT_RISK]: 'orange',
  [SystemCohortType.CHURN_CRITICAL]: 'red',
  [SystemCohortType.POWER_USERS]: 'green',
  [SystemCohortType.NEW_USERS]: 'blue',
  [SystemCohortType.SOCIAL_CONNECTORS]: 'purple',
  [SystemCohortType.LAPSED_MEMBERS]: 'orange',
  [SystemCohortType.WEEKEND_WARRIORS]: 'teal',
  [SystemCohortType.PAYMENT_ISSUES]: 'red',
};

/**
 * Chart colors for the churn distribution chart (ordered: SAFE, WATCH, AT_RISK, CRITICAL).
 */
export const CHURN_CHART_COLORS = ['#01B574', '#FFB547', '#FF6B35', '#E53E3E'];
